import React, { useState, useEffect } from 'react';
import avevaApi from '@services/avevaApiService';

interface AvevaStreamsProps {
  onSelectStream: (streamId: string) => void;
}

interface Stream {
  Id: string;
  TypeId?: string;
  Description?: string;
  Name?: string;
}

interface Pagination {
  skip: number;
  count: number;
}

const AvevaStreams = ({ onSelectStream }: AvevaStreamsProps) => {
  const [streams, setStreams] = useState<Stream[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [pagination, setPagination] = useState<Pagination>({ skip: 0, count: 20 });

  useEffect(() => {
    loadStreams();
  }, [pagination]);

  const loadStreams = async () => {
    setLoading(true);
    try {
      const response = await avevaApi.getStreams(pagination);
      setStreams(response.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return loadStreams();
    setLoading(true);
    try {
      const response = await avevaApi.searchStreams(searchQuery);
      setStreams(response.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const nextPage = () => setPagination((p) => ({ ...p, skip: p.skip + p.count }));
  const prevPage = () => setPagination((p) => ({ ...p, skip: Math.max(0, p.skip - p.count) }));

  return (
    <div className="aveva-streams">
      <div className="streams-header">
        <h2>AVEVA Streams</h2>
        <div className="search-controls">
          <input
            type="text"
            placeholder="Search streams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch}>Search</button>
          <button onClick={loadStreams}>Clear</button>
        </div>
      </div>

      {loading && <div className="loading">Loading streams...</div>}
      {error && <div className="error">Error: {error}</div>}

      {!loading && streams.length > 0 && (
        <>
          <table>
            <thead>
              <tr>
                <th>Stream ID</th>
                <th>Type</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {streams.map((s) => (
                <tr key={s.Id}>
                  <td>{s.Id}</td>
                  <td>{s.TypeId || 'N/A'}</td>
                  <td>{s.Description || s.Name || 'No description'}</td>
                  <td>
                    <button className="view-btn" onClick={() => onSelectStream(s.Id)}>
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="pagination">
            <button onClick={prevPage} disabled={pagination.skip === 0}>
              Previous
            </button>
            <span>
              Showing {pagination.skip + 1} - {pagination.skip + streams.length}
            </span>
            <button onClick={nextPage} disabled={streams.length < pagination.count}>
              Next
            </button>
          </div>
        </>
      )}

      {!loading && streams.length === 0 && (
        <div className="no-data">
          {searchQuery ? 'No streams found for your search.' : 'No streams available.'}
        </div>
      )}
    </div>
  );
};

export default AvevaStreams;
